import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO } from "@/hooks/usePageSEO";

const LAST_UPDATED = "June 2, 2026";

export default function TermsOfService() {
  const { t } = useLanguage();
  usePageSEO({
    title: "Terms of Service – ISO27001KIT",
    description: "Terms and conditions for using ISO27001KIT services, tools, and document packs.",
  });
  
  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4 text-foreground">{t('terms.title')}</h1>
        <div className="h-1 w-24 bg-primary mb-8"></div>
        
        <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-foreground">
          <p className="text-sm text-muted-foreground">{t('terms.lastUpdated')} {LAST_UPDATED}</p>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.acceptance.title')}</h2>
            <p>{t('terms.acceptance.content')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.services.title')}</h2>
            <p>{t('terms.services.intro')}</p>
            
            <h3 className="text-xl font-semibold mt-4">{t('terms.services.free.title')}</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('terms.services.free.pdfs')}</li>
              <li>{t('terms.services.free.preview')}</li>
              <li>{t('terms.services.free.exports')}</li>
            </ul>

            <h3 className="text-xl font-semibold mt-4">{t('terms.services.pro.title')}</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('terms.services.pro.tools')}</li>
              <li>{t('terms.services.pro.results')}</li>
              <li>{t('terms.services.pro.exports')}</li>
              <li>{t('terms.services.pro.saveLoad')}</li>
            </ul>

            <h3 className="text-xl font-semibold mt-4">{t('terms.services.wordPack.title')}</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('terms.services.wordPack.docs')}</li>
              <li>{t('terms.services.wordPack.noWatermark')}</li>
              <li>{t('terms.services.wordPack.zip')}</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.registration.title')}</h2>
            <p>{t('terms.registration.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('terms.registration.accurate')}</li>
              <li>{t('terms.registration.secure')}</li>
              <li>{t('terms.registration.responsible')}</li>
              <li>{t('terms.registration.notify')}</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.payments.title')}</h2>
            
            <h3 className="text-xl font-semibold mt-4">{t('terms.payments.pro.title')}</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('terms.payments.pro.paypal')}</li>
              <li>{t('terms.payments.pro.renew')}</li>
              <li>{t('terms.payments.pro.cancel')}</li>
              <li>{t('terms.payments.pro.retain')}</li>
            </ul>

            <h3 className="text-xl font-semibold mt-4">{t('terms.payments.wordPack.title')}</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('terms.payments.wordPack.oneTime')}</li>
              <li>{t('terms.payments.wordPack.immediate')}</li>
              <li>{t('terms.payments.wordPack.lifetime')}</li>
            </ul>

            <h3 className="text-xl font-semibold mt-4">{t('terms.payments.refunds.title')}</h3>
            <p>
              {t('terms.payments.refunds.intro')}{" "}
              <Link to="/refund-policy" className="text-primary underline underline-offset-2 hover:text-primary/80">
                {t('terms.payments.refunds.link')}
              </Link>{" "}
              {t('terms.payments.refunds.outro')}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.license.title')}</h2>
            
            <h3 className="text-xl font-semibold">{t('terms.license.permitted.title')}</h3>
            <p>{t('terms.license.permitted.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('terms.license.permitted.download')}</li>
              <li>{t('terms.license.permitted.tools')}</li>
              <li>{t('terms.license.permitted.modify')}</li>
              <li>{t('terms.license.permitted.export')}</li>
            </ul>

            <h3 className="text-xl font-semibold mt-4">{t('terms.license.restrictions.title')}</h3>
            <p>{t('terms.license.restrictions.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('terms.license.restrictions.resell')}</li>
              <li>{t('terms.license.restrictions.share')}</li>
              <li>{t('terms.license.restrictions.automate')}</li>
              <li>{t('terms.license.restrictions.claim')}</li>
              <li>{t('terms.license.restrictions.remove')}</li>
              <li>{t('terms.license.restrictions.commercial')}</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.disclaimer.title')}</h2>
            <p>{t('terms.disclaimer.content')}</p>
            <p>{t('terms.disclaimer.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('terms.disclaimer.requirements')}</li>
              <li>{t('terms.disclaimer.certification')}</li>
              <li>{t('terms.disclaimer.errorFree')}</li>
              <li>{t('terms.disclaimer.uninterrupted')}</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.professional.title')}</h2>
            <p>{t('terms.professional.content')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.liability.title')}</h2>
            <p>{t('terms.liability.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('terms.liability.use')}</li>
              <li>{t('terms.liability.errors')}</li>
              <li>{t('terms.liability.audits')}</li>
              <li>{t('terms.liability.breaches')}</li>
              <li>{t('terms.liability.payments')}</li>
              <li>{t('terms.liability.other')}</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.ip.title')}</h2>
            <p>{t('terms.ip.content')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.userResponsibility.title')}</h2>
            <p>{t('terms.userResponsibility.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('terms.userResponsibility.review')}</li>
              <li>{t('terms.userResponsibility.verify')}</li>
              <li>{t('terms.userResponsibility.compliance')}</li>
              <li>{t('terms.userResponsibility.security')}</li>
              <li>{t('terms.userResponsibility.advice')}</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.modifications.title')}</h2>
            <p>{t('terms.modifications.content')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.termination.title')}</h2>
            <p>{t('terms.termination.content')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.indemnification.title')}</h2>
            <p>{t('terms.indemnification.content')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.governing.title')}</h2>
            <p>{t('terms.governing.content')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.changes.title')}</h2>
            <p>{t('terms.changes.content')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('terms.contact.title')}</h2>
            <p>{t('terms.contact.intro')}</p>
            <p className="font-semibold">
              {t('terms.contact.email')}{" "}
              <a href="mailto:contact@iso27001kit.com" className="text-primary underline underline-offset-2 hover:text-primary/80">
                contact@iso27001kit.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
